import { useState, useEffect, useCallback } from 'react';
import { taxReturnAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Landmark, FileText, FileSpreadsheet, FileCode2, Loader2, Sparkles,
  TriangleAlert, Scale, Info, Wallet, TrendingUp, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (val) =>
  new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);

const FORMS = [
  { key: 'euer', label: 'Anlage EÜR', hint: '§ 4 Abs. 3 EStG' },
  { key: 'ust_annual', label: 'USt-Jahreserklärung', hint: 'USt 2 A' },
  { key: 'ustva', label: 'Voranmeldung', hint: 'USt 1 A' },
];

function FormRow({ line, kz, label, amount, note, extra }) {
  return (
    <TableRow className="border-border/20">
      <TableCell className="font-mono-data text-xs text-muted-foreground w-[52px]">{line ?? '—'}</TableCell>
      <TableCell className="font-mono-data text-xs text-primary w-[52px]">{kz || '—'}</TableCell>
      <TableCell className="text-sm">
        {label}
        {note && <div className="text-[11px] text-muted-foreground mt-0.5">{note}</div>}
        {extra && <div className="text-[11px] text-muted-foreground mt-0.5 font-mono-data">{extra}</div>}
      </TableCell>
      <TableCell className="text-right font-mono-data text-sm whitespace-nowrap">{fmt(amount)}</TableCell>
    </TableRow>
  );
}

function SumRow({ line, kz, label, amount }) {
  return (
    <TableRow className="border-border/30 bg-primary/5">
      <TableCell className="font-mono-data text-xs text-muted-foreground">{line}</TableCell>
      <TableCell className="font-mono-data text-xs text-primary">{kz}</TableCell>
      <TableCell className="text-sm font-bold">{label}</TableCell>
      <TableCell className="text-right font-mono-data text-sm font-bold whitespace-nowrap">{fmt(amount)}</TableCell>
    </TableRow>
  );
}

function FormTableHeader() {
  return (
    <TableHeader>
      <TableRow className="border-border/30">
        <TableHead className="text-xs">Zeile</TableHead>
        <TableHead className="text-xs">Kz</TableHead>
        <TableHead className="text-xs">Bezeichnung</TableHead>
        <TableHead className="text-xs text-right">Betrag EUR</TableHead>
      </TableRow>
    </TableHeader>
  );
}

export default function TaxReturnPage() {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(null);
  const [basis, setBasis] = useState('ist');
  const [period, setPeriod] = useState('quarterly');
  const [form, setForm] = useState('euer');
  const [euer, setEuer] = useState(null);
  const [ustAnnual, setUstAnnual] = useState(null);
  const [ustva, setUstva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [categorizing, setCategorizing] = useState(false);

  useEffect(() => {
    taxReturnAPI.years().then((res) => {
      setYears(res.data.years || []);
      setYear(res.data.default_year);
    }).catch(() => {
      const y = new Date().getFullYear() - 1;
      setYears([y + 1, y]);
      setYear(y);
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const [e, u, v] = await Promise.all([
        taxReturnAPI.euer(year, basis),
        taxReturnAPI.ustAnnual(year),
        taxReturnAPI.ustvaKz(year, period),
      ]);
      setEuer(e.data);
      setUstAnnual(u.data);
      setUstva(v.data);
    } catch {
      toast.error('Steuerdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [year, basis, period]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const res = await taxReturnAPI.download({ form, year, format, basis, period });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `Steuerexport_${year}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export als ${format.toUpperCase()} erstellt`);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setDownloading('');
    }
  };

  const handleCategorize = async () => {
    setCategorizing(true);
    try {
      const res = await taxReturnAPI.categorizePending();
      if (res.data.categorized > 0) {
        toast.success(`${res.data.categorized} Rechnung(en) per KI zugeordnet`);
        loadData();
      } else {
        toast.info('Alle Belege sind bereits zugeordnet');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KI-Zuordnung fehlgeschlagen');
    } finally {
      setCategorizing(false);
    }
  };

  const uncategorized = euer?.stats?.uncategorized_expenses || 0;

  return (
    <div className="space-y-8" data-testid="tax-return-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2 flex items-center gap-3">
            <Landmark className="w-7 h-7 text-primary" />
            Steuererklärung
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl">
            Anlage EÜR, Umsatzsteuer-Jahreserklärung und Voranmeldung – aufgebaut nach den amtlichen
            Zeilen und Kennzahlen. Export als PDF, CSV und ELSTER-orientiertes XML.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={year ? String(year) : ''} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[120px] bg-background" data-testid="tax-year-select">
              <SelectValue placeholder="Jahr" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger className="w-[210px] bg-background" data-testid="tax-basis-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ist">Zuflussprinzip (§ 11 EStG)</SelectItem>
              <SelectItem value="soll">Rechnungsdatum (Soll)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      {euer && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="tax-summary-cards">
          <Card className="bg-card border border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Betriebseinnahmen (Kz 159)
              </div>
              <div className="font-mono-data text-2xl font-bold text-emerald-400" data-testid="tax-income-total">
                {fmt(euer.income_total.amount)} €
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{euer.stats.income_documents} Rechnungen</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-red-400" /> Betriebsausgaben (Kz 199)
              </div>
              <div className="font-mono-data text-2xl font-bold text-red-400" data-testid="tax-expense-total">
                {fmt(euer.expense_total.amount)} €
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{euer.stats.expense_documents} Belege</p>
            </CardContent>
          </Card>
          <Card className="bg-card border border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Scale className="w-3.5 h-3.5 text-primary" /> Gewinn/Verlust (Kz 219)
              </div>
              <div className={`font-mono-data text-2xl font-bold ${euer.profit >= 0 ? 'text-primary' : 'text-red-400'}`} data-testid="tax-profit">
                {fmt(euer.profit)} €
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {euer.tax_type === 'kleinunternehmer' ? 'Kleinunternehmer § 19 UStG' : 'Regelbesteuerung'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unzugeordnete Belege */}
      {uncategorized > 0 && (
        <Card className="bg-amber-500/5 border border-amber-500/30" data-testid="uncategorized-warning">
          <CardContent className="p-4 flex items-start gap-3 flex-wrap">
            <TriangleAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[240px]">
              <p className="text-sm font-medium text-amber-300">
                {uncategorized} Beleg(e) ohne amtliche EÜR-Zuordnung
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Sie laufen derzeit in Zeile 60 / Kz 183 („Übrige unbeschränkt abziehbare Betriebsausgaben“).
                Lass die KI zuordnen und korrigiere bei Bedarf in den Eingangsrechnungen.
              </p>
            </div>
            <Button
              onClick={handleCategorize}
              disabled={categorizing}
              className="bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25"
              data-testid="ai-categorize-btn"
            >
              {categorizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              KI-Zuordnung starten
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Formulare */}
      <Card className="bg-card border border-border/50">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Amtliche Formulare {year}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => handleDownload('pdf')} disabled={!!downloading}
                className="border-primary/30 text-primary hover:bg-primary/10" data-testid="export-pdf-btn">
                {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                PDF
              </Button>
              <Button variant="outline" onClick={() => handleDownload('csv')} disabled={!!downloading}
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="export-csv-btn">
                {downloading === 'csv' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                CSV
              </Button>
              <Button variant="outline" onClick={() => handleDownload('xml')} disabled={!!downloading}
                className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10" data-testid="export-xml-btn">
                {downloading === 'xml' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCode2 className="w-4 h-4 mr-2" />}
                XML
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={form} onValueChange={setForm}>
            <TabsList className="bg-muted/50 flex-wrap h-auto" data-testid="tax-form-tabs">
              {FORMS.map((f) => (
                <TabsTrigger key={f.key} value={f.key} data-testid={`tab-${f.key}`} className="text-xs sm:text-sm">
                  {f.label}
                  <span className="hidden sm:inline text-[10px] text-muted-foreground ml-1.5 font-mono-data">{f.hint}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {loading && (
              <div className="py-16 flex items-center justify-center text-muted-foreground gap-2" data-testid="tax-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Berechne amtliche Kennzahlen…
              </div>
            )}

            {/* Anlage EÜR */}
            <TabsContent value="euer" className="mt-5">
              {!loading && euer && (
                <div className="space-y-6" data-testid="euer-panel">
                  <p className="text-xs text-muted-foreground font-mono-data">{euer.legal_basis}</p>
                  <div>
                    <h3 className="text-sm font-bold mb-2 text-emerald-400">1. Betriebseinnahmen</h3>
                    <div className="table-responsive">
                      <Table>
                        <FormTableHeader />
                        <TableBody>
                          {euer.income_rows.map((r) => <FormRow key={`${r.line}-${r.kz}`} {...r} />)}
                          <SumRow {...euer.income_total} />
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-2 text-red-400">2. Betriebsausgaben</h3>
                    <div className="table-responsive">
                      <Table>
                        <FormTableHeader />
                        <TableBody>
                          {euer.expense_rows.map((r) => (
                            <FormRow
                              key={`${r.line}-${r.kz}`}
                              {...r}
                              extra={r.not_deductible > 0
                                ? `Aufwendungen insgesamt ${fmt(r.total)} € · abziehbar ${fmt(r.deductible)} €`
                                : null}
                            />
                          ))}
                          <SumRow {...euer.expense_total} />
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold mb-2 text-primary">3. Ermittlung des Gewinns</h3>
                    <div className="table-responsive">
                      <Table>
                        <FormTableHeader />
                        <TableBody>
                          {euer.profit_rows.slice(0, -1).map((r) => <FormRow key={r.line} {...r} />)}
                          <SumRow {...euer.profit_rows[euer.profit_rows.length - 1]} />
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* USt-Jahreserklärung */}
            <TabsContent value="ust_annual" className="mt-5">
              {!loading && ustAnnual && (
                <div className="space-y-5" data-testid="ust-annual-panel">
                  <p className="text-xs text-muted-foreground font-mono-data">{ustAnnual.legal_basis}</p>
                  <div className="table-responsive">
                    <Table>
                      <FormTableHeader />
                      <TableBody>
                        {ustAnnual.rows.map(({ key: rowKey, ...r }) => <FormRow key={rowKey} {...r} />)}
                      </TableBody>
                    </Table>
                  </div>
                  <Separator className="bg-border/40" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Card className="bg-card border border-red-500/20">
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground mb-1">Umsatzsteuer gesamt</p>
                        <p className="font-mono-data text-lg font-bold text-red-400">{fmt(ustAnnual.summary.umsatzsteuer)} €</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-card border border-emerald-500/20">
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground mb-1">Vorsteuer gesamt (Kz 637)</p>
                        <p className="font-mono-data text-lg font-bold text-emerald-400">{fmt(ustAnnual.summary.vorsteuer)} €</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-card border border-primary/30">
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <Wallet className="w-3.5 h-3.5" /> Verbleibende USt / Erstattung
                        </p>
                        <p className="font-mono-data text-lg font-bold text-primary" data-testid="ust-annual-zahllast">
                          {fmt(ustAnnual.summary.zahllast)} €
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* UStVA */}
            <TabsContent value="ustva" className="mt-5">
              {!loading && ustva && (
                <div className="space-y-4" data-testid="ustva-kz-panel">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="text-xs text-muted-foreground font-mono-data">{ustva.legal_basis}</p>
                    <Select value={period} onValueChange={setPeriod}>
                      <SelectTrigger className="w-[180px] bg-background" data-testid="ustva-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monatlich</SelectItem>
                        <SelectItem value="quarterly">Vierteljährlich</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="table-responsive">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/30">
                          <TableHead className="text-xs">Zeitraum</TableHead>
                          {ustva.lines.map((l) => (
                            <TableHead key={l.kz} className="text-xs text-right font-mono-data" title={l.label}>
                              Kz {l.kz}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ustva.periods.map((p) => (
                          <TableRow key={p.key} className="border-border/20" data-testid={`ustva-period-${p.key}`}>
                            <TableCell className="text-sm whitespace-nowrap">{p.label}</TableCell>
                            {ustva.lines.map((l) => (
                              <TableCell key={l.kz} className={`text-right font-mono-data text-xs ${l.key === 'zahllast' ? 'font-bold text-primary' : ''}`}>
                                {fmt(p.values[l.key])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="space-y-1.5 pt-2">
                    {ustva.lines.map((l) => (
                      <div key={l.kz} className="flex gap-3 text-xs text-muted-foreground">
                        <span className="font-mono-data text-primary w-10 flex-shrink-0">Kz {l.kz}</span>
                        <span>{l.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Rechtlicher Hinweis */}
      <Card className="bg-muted/20 border border-border/40" data-testid="legal-note-card">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground leading-relaxed">
            {euer?.legal_note}
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline" className="text-[10px] border-border/60">Anlage EÜR – amtliche Zeilen & Kennzahlen</Badge>
              <Badge variant="outline" className="text-[10px] border-border/60">USt 2 A – Kz 177, 275, 320, 467, 637</Badge>
              <Badge variant="outline" className="text-[10px] border-border/60">USt 1 A – Kz 81, 86, 66, 83</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
